import Animated, {
  interpolate,
  useAnimatedStyle,
  useDerivedValue,
  withTiming,
} from 'react-native-reanimated';
import React, { useState } from 'react';
import { AnimatedSkeletonLoader } from '@usepepper/ui/atoms/animated-skeleton-loader';
import { createStyles, useTheme } from '@/contexts/theme-context';
import { DEFAULT_EASING_IN_OUT_CONFIG } from '@experiences/blocs/utils/animations';
import { DropdownMenu, Icons } from '@usepepper/sprout-kit-ui';
import { Pressable, View } from 'react-native';
import { SvgProps } from 'react-native-svg';
import { Typography } from '@usepepper/ui/atoms/typography';

const ICON_SIZE = 16;
const CHEVRON_ICON_SIZE = 14;
const SKELETON_LABEL_WIDTH = 122;
const SKELETON_LABEL_HEIGHT = 14;

interface QuickActionItemBase {
  Icon: React.FC<SvgProps>;
  label: string;
}

export interface QuickActionPressItem extends QuickActionItemBase {
  dropdownContent?: never;
  onOpenChange?: never;
  onPress: () => void;
}

export interface QuickActionDropdownItem extends QuickActionItemBase {
  dropdownContent: (close: () => void) => React.ReactNode;
  onOpenChange?: (open: boolean) => void;
  onPress?: never;
}

export type QuickActionItem = QuickActionPressItem | QuickActionDropdownItem;

interface QuickActionsCardProps {
  actions: QuickActionItem[];
  loading?: boolean;
  skeletonRowCount: number;
}

const AnimatedChevron: React.FC<{ isOpen: boolean }> = ({ isOpen }) => {
  const theme = useTheme();
  const rotationProgress = useDerivedValue(() =>
    withTiming(isOpen ? 1 : 0, DEFAULT_EASING_IN_OUT_CONFIG),
  );
  const chevronStyle = useAnimatedStyle(() => ({
    transform: [
      { rotate: `${interpolate(rotationProgress.value, [0, 1], [0, 180])}deg` },
    ],
  }));

  return (
    <Animated.View style={chevronStyle}>
      <Icons.ChevronDown
        color={theme.sk.color.icon.secondary}
        size={CHEVRON_ICON_SIZE}
      />
    </Animated.View>
  );
};

export const QuickActionsCard: React.FC<QuickActionsCardProps> = ({
  actions,
  loading,
  skeletonRowCount,
}) => {
  const [styles, theme] = useStyles();
  const borderRadius = theme.sk.radii.radiusRound;
  const [openDropdownLabel, setOpenDropdownLabel] = useState<string | null>(
    null,
  );

  if (loading) {
    return (
      <View style={styles.card}>
        {Array.from({ length: skeletonRowCount }).map((_, index) => (
          <View key={index} style={styles.actionRow}>
            <AnimatedSkeletonLoader
              borderRadius={borderRadius}
              height={SKELETON_LABEL_HEIGHT}
              width={SKELETON_LABEL_WIDTH}
            />
          </View>
        ))}
      </View>
    );
  }

  return (
    <View style={styles.card}>
      {actions.map(
        ({ dropdownContent, Icon, label, onOpenChange, onPress }) => {
          const rowContent = (
            <>
              <View style={styles.iconContainer}>
                <Icon
                  height={ICON_SIZE}
                  stroke={theme.sk.color.text.primary}
                  width={ICON_SIZE}
                />
              </View>
              <Typography
                color={theme.sk.color.text.primary}
                flexShrink={1}
                numberOfLines={1}
                variant="paragraph-md"
              >
                {label}
              </Typography>
            </>
          );

          if (dropdownContent) {
            const isOpen = openDropdownLabel === label;

            return (
              <DropdownMenu
                key={label}
                onOpenChange={(open) => {
                  setOpenDropdownLabel(open ? label : null);
                  onOpenChange?.(open);
                }}
                open={isOpen}
                placement="bottom-start"
              >
                <DropdownMenu.Trigger asChild>
                  <Pressable
                    accessibilityRole="button"
                    accessibilityState={{ expanded: isOpen }}
                    style={styles.actionRow}
                  >
                    {rowContent}
                    <View style={styles.chevronContainer}>
                      <AnimatedChevron isOpen={isOpen} />
                    </View>
                  </Pressable>
                </DropdownMenu.Trigger>
                {dropdownContent(() => setOpenDropdownLabel(null))}
              </DropdownMenu>
            );
          }

          return (
            <Pressable
              accessibilityRole="button"
              key={label}
              onPress={onPress}
              style={styles.actionRow}
            >
              {rowContent}
            </Pressable>
          );
        },
      )}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  actionRow: {
    alignItems: 'center',
    borderRadius: theme.sk.radii.radiusSmall,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSMedium,
    height: theme.sk.spacing.spaceXlarge,
    overflow: 'hidden',
  },
  card: {
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    paddingVertical: theme.sk.spacing.spaceXsmall,
  },
  chevronContainer: {
    flexShrink: 0,
    marginLeft: 'auto',
  },
  iconContainer: {
    flexShrink: 0,
    height: ICON_SIZE,
    width: ICON_SIZE,
  },
}));
